import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { apiService } from '../services/api'

const AuthContext = createContext()

const initialState = {
  user: null,
  profile: null,
  isAuthenticated: false,
  isAdmin: false,
  loading: true,
  error: null
}

function authReducer(state, action) {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, loading: true, error: null }
    
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        profile: action.payload.profile,
        isAuthenticated: true,
        isAdmin: action.payload.profile?.role === 'admin',
        loading: false,
        error: null
      }
    
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        profile: null,
        isAuthenticated: false,
        isAdmin: false,
        loading: false,
        error: action.payload
      }
    
    case 'AUTH_LOGOUT':
      return {
        ...initialState,
        loading: false
      }
    
    default:
      return state
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  useEffect(() => {
    // Verificar si hay token guardado al cargar la app
    const token = localStorage.getItem('authToken')
    const userData = localStorage.getItem('userData')
    
    if (token && userData) {
      try {
        const parsed = JSON.parse(userData)
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: parsed
        })
      } catch (error) {
        console.error('Error parsing stored user data:', error)
        localStorage.removeItem('authToken')
        localStorage.removeItem('userData')
        dispatch({ type: 'AUTH_FAILURE', payload: 'Invalid stored session' })
      }
    } else {
      dispatch({ type: 'AUTH_FAILURE', payload: null })
    }
  }, [])

  const login = async (email, password) => {
    dispatch({ type: 'AUTH_START' })
    
    try {
      const response = await apiService.login(email, password)
      
      // Guardar datos del usuario
      localStorage.setItem('userData', JSON.stringify({
        user: response.user,
        profile: response.profile
      }))
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: response.user,
          profile: response.profile
        }
      })
      
      return response
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error.message })
      throw error
    }
  }

  const logout = async () => {
    try {
      await apiService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('userData')
      dispatch({ type: 'AUTH_LOGOUT' })
    }
  }

  const value = {
    ...state,
    login,
    logout
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}